// Раскладка картинок по роль-слотам режима.
//
// Режимы с `mode.imageRoles` принимают кадры не пулом, а по ролям, и порядок в
// `inputImageUrls` обязан повторять порядок ролей: бэк раскладывает
// `inputImageUrls[i]` в канал `imageRoles[i]`. Роль живёт на pending-вложении
// (`PendingUpload.role`), а до старта генерации доезжают уже `ChatAttachment` —
// связываем их по `s3Key`, единственному ключу, общему для обеих сторон.

import type { ImageInputRole } from "~/shared/data/chat-models";
import type { PendingUpload } from "~/shared/store/types";

export interface RoleAwareImage {
    s3Key: string;
    s3Url: string;
}

/** Карта `s3Key → роль` по pending-вложениям чата (без роли — не попадают). */
export function buildRoleByS3Key(pending: readonly PendingUpload[]): Map<string, ImageInputRole> {
    const map = new Map<string, ImageInputRole>();
    for (const item of pending) {
        if (item.s3Key && item.role) map.set(item.s3Key, item.role);
    }
    return map;
}

/**
 * Ключи вложений, положенных в колонку референсов.
 *
 * Канал хранится там же, где роль кадра, — на самом pending-вложении, и по той
 * же причине: до отправки доезжают `ChatAttachment`, у которых своего понятия
 * канала нет, а `s3Key` общий для обеих сторон. Без этой карты референс уехал бы
 * кадром — то есть другим запросом за те же деньги.
 */
export function buildReferenceS3Keys(pending: readonly PendingUpload[]): Set<string> {
    const keys = new Set<string>();
    for (const item of pending) {
        if (item.s3Key && item.channel === "reference") keys.add(item.s3Key);
    }
    return keys;
}

export interface InputChannels {
    inputImageUrls: string[];
    referenceImageUrls: string[];
}

/**
 * Картиночные входы, разложенные по каналам запроса: кадры (в порядке ролей, если
 * они у режима есть) и референсы отдельным полем.
 *
 * Референс, уехавший кадром, — другой запрос за те же деньги: провайдер сделал бы
 * из него первый или последний кадр, а лишние отбросил молча.
 */
export function buildImageChannels(
    images: readonly RoleAwareImage[],
    roles: readonly ImageInputRole[],
    pending: readonly PendingUpload[],
): InputChannels {
    const referenceKeys = buildReferenceS3Keys(pending);
    const frames = images.filter((image) => !referenceKeys.has(image.s3Key));
    return {
        inputImageUrls:
            roles.length > 0
                ? orderImageUrlsByRole(frames, roles, buildRoleByS3Key(pending))
                : frames.map((image) => image.s3Url),
        referenceImageUrls: images
            .filter((image) => referenceKeys.has(image.s3Key))
            .map((image) => image.s3Url),
    };
}

/**
 * URL картинок в порядке ролей режима.
 *
 * Останавливается на первой незаполненной роли: пропуск в середине сдвинул бы
 * последующие кадры на чужие каналы (последний кадр приехал бы первым). UI не
 * даёт создать такой пропуск — слот роли открывается только после предыдущего, —
 * а здесь стоит второй барьер, чтобы кривой стейт не уехал в провайдера.
 * Картинки без роли (например, добавленные до переключения режима) игнорируются.
 */
export function orderImageUrlsByRole(
    images: readonly RoleAwareImage[],
    roles: readonly ImageInputRole[],
    roleByS3Key: ReadonlyMap<string, ImageInputRole>,
): string[] {
    const urls: string[] = [];
    for (const role of roles) {
        const match = images.find((image) => roleByS3Key.get(image.s3Key) === role);
        if (!match) break;
        urls.push(match.s3Url);
    }
    return urls;
}
